import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { Bell, HelpCircle, MessageSquare } from "lucide-react";
import AlertDropdown from "../alerts/AlertDropdown";
import ChatPanel from "../chat/ChatPanel";
import { setUnreadListener } from "../../api/axios";
import Menu from "./Menu";
import "../alerts/AlertDropdown.css";

function Header() {
  const navigate = useNavigate();
  const projects = JSON.parse(localStorage.getItem("projects") || "[]");
  const projectId = localStorage.getItem("projectId");

  const goDashboard = () =>
    projectId ? navigate(`/project/${projectId}`) : navigate("/select-project");

  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const notificationButtonRef = useRef(null);
  const [ringBell, setRingBell] = useState(false);
  const prevUnreadCount = useRef(0);
  const isInitialized = useRef(false);

  useEffect(() => {
    setUnreadListener((count) => {
      // 초기화 후에만 새 알림 체크
      if (isInitialized.current && count > prevUnreadCount.current) {
        setRingBell(true);
        setTimeout(() => setRingBell(false), 800);
      }
      prevUnreadCount.current = count;
      setUnreadCount(count);
      isInitialized.current = true;
    });
  }, []);

  const titleRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    if (!projectId) return;
    const match = projects.find((p) => String(p.id) === String(projectId));
    if (match) setProjectName(match.name);
  }, [projectId, projects]);

  useEffect(() => {
    if (!titleRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const w = entry.contentRect.width;
        setIsCompact(w < 260);
      }
    });
    ro.observe(titleRef.current);
    return () => ro.disconnect();
  }, []);

  let titleText = "";
  if (projectName) titleText = `${projectName} PMIS`;
  else titleText = isCompact ? "PMIS" : "Project Management Information System";

  return (
    <nav className="w-full bg-[#1e1e2f] no-print">
      <div className="w-full px-6 py-3 flex items-center">
        <div className="flex items-center shrink-0 text-white whitespace-nowrap text-xl font-bold">
          <button
            ref={titleRef}
            onClick={goDashboard}
            className="hover:opacity-90"
          >
            {titleText}
          </button>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-4 text-white">
          <div className="flex items-center gap-4 whitespace-nowrap shrink-0 no-scrollbar">
            <Menu />
          </div>

          {/* 알림 */}
          {projectId && (
            <div className="relative shrink-0">
              <button
                ref={notificationButtonRef}
                onClick={() => setOpen(!open)}
                className="relative p-2 rounded-full hover:bg-gray-700 transition-colors"
              >
                <Bell size={20} className={ringBell ? "ring-bell" : ""} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1 py-0.5 rounded-full pulse-badge">
                    {unreadCount}
                  </span>
                )}
              </button>

              <AlertDropdown
                isOpen={open}
                setOpen={setOpen}
                setUnreadCount={setUnreadCount}
                buttonRef={notificationButtonRef}
              />

              {/* 💬 채팅 */}
              <button
                onClick={() => setIsChatOpen(true)}
                className="p-2 ml-2 rounded-full hover:bg-gray-700 transition relative"
                title="채팅"
              >
                <MessageSquare size={20} />
                {chatUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] px-1 py-0.5 min-w-[17px] h-[17px] rounded-full flex items-center justify-center pulse-badge">
                    {chatUnreadCount}
                  </span>
                )}
              </button>

              {/* ❓ 도움말 / FAQ */}
              <button
                onClick={() => navigate(`/project/${projectId}/system/faq`)}
                className="p-2 ml-2 rounded-full hover:bg-gray-700 transition"
                title="도움말 / FAQ"
              >
                <HelpCircle size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
      <ChatPanel
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onOpenExternally={() => setIsChatOpen(true)}
        onUnreadCountChange={setChatUnreadCount}
      />
    </nav>
  );
}

export default Header;
